import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Breadcrumb, Button } from "flowbite-react";
import { HiHome, HiPlus } from "react-icons/hi";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { useGetClientsQuery, useCreateClientMutation } from "@/api/clientApi/client";
import { Client, DataGridPagination, QueryParams } from "@/sharedTypes";
import ClientFilters from "@/components/clients/ClientFilters";
import ClientListTable from "@/components/clients/ClientListTable";
import ClientFormDialog from "@/components/clients/ClientFormDialog";
import LoadingPage from "@/components/LoadingPage";
import { toast } from "@/lib/toast";
import { flattenQueryParams, getErrorMessage } from "@/utils/requestUtils";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles, isCompanyAdminUser } from "@/constants/agent";
import { useSelectedCompany } from "@/context/SelectedCompanyContext";
import SelectCompanyPrompt from "@/components/SelectCompanyPrompt";
import SuperAdminCompanyBar from "@/components/SuperAdminCompanyBar";

export default function ClientList() {
  const { t } = useTranslation(["client", "common"]);
  const user = useCurrentUser();
  // Super admin scopes the client list to the company picked in the header.
  const isSuperAdmin = user?.agent_role === agentRoles.super_admin;
  const { selectedCompany } = useSelectedCompany();
  const needsCompany = isSuperAdmin && !selectedCompany;
  const [showInactive, setShowInactive] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  // Deep link from the activity log: /clients?id=42 opens the list on that record.
  const [searchParams] = useSearchParams();
  const linkedId = searchParams.get("id");
  const [queryParams, setQueryParams] = useState<QueryParams>({
    filters: linkedId ? { client_id: linkedId } : { is_active: true },
    pagination: {
      page: 1,
      pageSize: 15,
    },
  });

  const flatQueryParams = flattenQueryParams(
    isSuperAdmin && selectedCompany
      ? {
          ...queryParams,
          filters: { ...queryParams.filters, company_id: selectedCompany.id },
        }
      : queryParams
  );
  const { data: clientsData, isLoading, isError, error } = useGetClientsQuery(
    flatQueryParams,
    { refetchOnMountOrArgChange: true, skip: needsCompany }
  );
  const [createClient] = useCreateClientMutation();

  useEffect(() => {
    if (isError) {
      toast.error(getErrorMessage(error));
    }
  }, [isError, error]);

  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        is_active: !showInactive,
      },
    }));
  }, [showInactive]);

  const handleCreate = (data: Client) => {
    createClient(data)
      .unwrap()
      .then(() => {
        toast.success(t("client:createSuccess"));
        setIsCreateModalOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleToggleInactive = (value: boolean) => {
    setShowInactive(value);
    setQueryParams((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        is_active: !value,
      },
      pagination: { ...prev.pagination, page: 1 },
    }));
  };

  const handleFilterChange = (filters: Record<string, any>) => {
    setQueryParams((prev) => ({
      ...prev,
      filters: {
        ...filters,
        is_active: !showInactive,
      },
      pagination: { ...prev.pagination, page: 1 },
    }));
  };

  const handlePageChange = (page: number) => {
    setQueryParams((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, page },
    }));
  };

  const responsePagination = clientsData?.pagination;
  const responseItems = clientsData?.items ?? clientsData?.data?.items ?? [];
  const totalItems =
    responsePagination?.totalItems ??
    responsePagination?.total ??
    clientsData?.totalItems ??
    responseItems.length ??
    0;
  const calculatedTotalPages =
    Math.ceil(
      totalItems /
        (responsePagination?.pageSize ||
          clientsData?.pageSize ||
          queryParams.pagination.pageSize)
    ) || 1;

  const pagination: DataGridPagination = {
    pageIndex:
      responsePagination?.pageIndex ||
      responsePagination?.page ||
      clientsData?.pageIndex ||
      queryParams.pagination.page,
    pageSize:
      responsePagination?.pageSize ||
      clientsData?.pageSize ||
      queryParams.pagination.pageSize,
    totalPages:
      responsePagination?.totalPages || calculatedTotalPages,
    totalItems,
  };

  return (
    <NavbarSidebarLayout>
      {isLoading ? <LoadingPage /> : <div className="h-screen overflow-y-auto pb-20">
        <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
          <div className="col-span-full space-y-6">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">{t("common:home")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("client:clients")}</Breadcrumb.Item>
            </Breadcrumb>

            <SuperAdminCompanyBar />

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  {t("client:clients")}
                </h1>
                <p className="text-gray-500">{t("client:manageClientsDescription")}</p>
              </div>
              {isCompanyAdminUser(user) && (
                <Button color="primary" onClick={() => setIsCreateModalOpen(true)}>
                  <HiPlus className="mr-2 h-5 w-5" />
                  {t("client:createClient")}
                </Button>
              )}
            </div>

            {needsCompany ? (
              <SelectCompanyPrompt />
            ) : (
              <>
                <ClientFilters
                  showInactive={showInactive}
                  onToggleInactive={handleToggleInactive}
                  onFilterChange={handleFilterChange}
                />

                <ClientListTable
                  data={responseItems}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  showInactive={showInactive}
                />
              </>
            )}
          </div>
        </div>
      </div>}

      <ClientFormDialog
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
        mode="create"
      />
    </NavbarSidebarLayout>
  );
}
